import { Link, TablePagination, Typography } from "@mui/material";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import WrapperLoading from "components/WrapperLoading";
import { SEARCH_QUERY_DEFAULT } from "constants/common";
import { SearchQuery } from "interfaces/common";
import { ReportDto } from "interfaces/report";
import { RevisionDto } from "interfaces/revision";
import { UserDto } from "interfaces/user";
import { isEmpty } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { revisionService } from "services/revisionService";

interface Props {
  report: ReportDto;
}

export default function RevisionTable({ report }: Props) {
  const [revisions, setRevisions] = useState<RevisionDto[]>([]);
  const [query, setQuery] = useState<SearchQuery>({
    ...SEARCH_QUERY_DEFAULT,
    perPage: 10,
    report: report._id,
    orderDirection: "desc",
  });
  const navigate = useNavigate();
  const [isLoadedRevisions, setIsLoadedRevisions] = useState<boolean>(true);
  const [total, setTotal] = useState<number>(0);

  useEffect(() => {
    getRevisionsOfReport();
  }, [query]);

  const getRevisionsOfReport = async () => {
    setIsLoadedRevisions(true);

    const response = await revisionService.search(query);

    setRevisions(response.data);
    setTotal(response.pagination.total);

    setIsLoadedRevisions(false);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setQuery({ ...query, page: 1, perPage: parseInt(event.target.value, 10) });
  };

  const handleChangePage = (_: any, newPage: number) => {
    setQuery({ ...query, page: newPage });
  };

  const renderContent = () => {
    if (isLoadedRevisions) {
      return <WrapperLoading />;
    }

    if (isEmpty(revisions)) {
      return (
        <Typography textAlign="center" marginTop={5} variant="h5">
          No data available.
        </Typography>
      );
    }

    return (
      <>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Edited By</TableCell>
                <TableCell>Previous Version</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {revisions.map((row) => (
                <TableRow
                  key={row._id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {moment(row.createdAt).format("ll")}
                  </TableCell>
                  <TableCell>
                    {moment(row.createdAt).format("HH:mm:ss")}
                  </TableCell>
                  <TableCell>
                    {(row.createdBy as UserDto).firstName}{" "}
                    {(row.createdBy as UserDto).lastName}
                  </TableCell>
                  <TableCell>
                    <Link
                      underline="always"
                      component="button"
                      onClick={() => navigate(`/revisions/${row._id}`)}
                    >
                      VIEW REPORT
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 20, 30]}
          component="div"
          count={total}
          rowsPerPage={query.perPage}
          page={query.page - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </>
    );
  };

  return renderContent();
}
